/**
 * Marks whatever is currently being scrolled with `.is-scrolling`, and clears
 * the mark a moment after scrolling stops.
 *
 * src/styles/scrollbars.css paints the scrollbar thumb only for that class, so
 * the thumb appears while the user scrolls with the wheel, trackpad or keyboard
 * and disappears again once they stop. CSS alone cannot express "is scrolling",
 * which is why this exists. Hover deliberately reveals nothing.
 *
 * One listener on the document in the capture phase covers every scroll region
 * in the app - scroll events do not bubble, but they are still captured on the
 * way down, so panels added later are handled without any wiring.
 */

const HIDE_DELAY_MS = 900;
const CLASS_NAME = "is-scrolling";

// Per-element hide timers; a WeakMap so detached nodes are collectable.
const timers = new WeakMap();

// The element whose thumb is currently being dragged, if any.
let draggedEl = null;

function resolveTarget(target) {
  // A scroll of the page itself reports the document, not an element.
  if (target === document || target === window) return document.documentElement;
  return target && target.nodeType === 1 ? target : null;
}

function show(el) {
  el.classList.add(CLASS_NAME);
  const pending = timers.get(el);
  if (pending) {
    clearTimeout(pending);
    timers.delete(el);
  }
}

function hideLater(el) {
  const pending = timers.get(el);
  if (pending) clearTimeout(pending);

  timers.set(
    el,
    setTimeout(() => {
      el.classList.remove(CLASS_NAME);
      timers.delete(el);
    }, HIDE_DELAY_MS)
  );
}

function handleScroll(event) {
  const el = resolveTarget(event.target);
  if (!el || !el.classList) return;

  show(el);
  if (el !== draggedEl) hideLater(el);
}

/**
 * Holds the thumb visible for the whole of a drag.
 *
 * Without this, pressing the thumb and pausing - or dragging past the end of
 * the scroll range, where no further scroll events fire - would let the thumb
 * fade out from under the pointer mid-drag.
 *
 * A press on the scrollbar lane lands on the element itself but outside its
 * padding box, which is what the offset comparison detects.
 */
function handlePointerDown(event) {
  if (event.button !== 0) return;

  const el = event.target;
  if (!el || el.nodeType !== 1 || !el.classList) return;

  const onVerticalLane = event.offsetX > el.clientWidth;
  const onHorizontalLane = event.offsetY > el.clientHeight;
  if (!onVerticalLane && !onHorizontalLane) return;

  draggedEl = el;
  show(el);
}

function handlePointerUp() {
  if (!draggedEl) return;
  hideLater(draggedEl);
  draggedEl = null;
}

/**
 * Starts the listeners. Returns a cleanup function.
 */
export default function initScrollAutoHide() {
  document.addEventListener("scroll", handleScroll, {
    capture: true,
    passive: true,
  });
  document.addEventListener("pointerdown", handlePointerDown, {
    capture: true,
    passive: true,
  });
  window.addEventListener("pointerup", handlePointerUp, { passive: true });
  window.addEventListener("pointercancel", handlePointerUp, { passive: true });

  return () => {
    document.removeEventListener("scroll", handleScroll, { capture: true });
    document.removeEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
  };
}